import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import Slider from "@mui/material/Slider";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TextField from "@mui/material/TextField";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Alert from "@mui/material/Alert";

import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/FullscreenExit";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import InvertColorsIcon from "@mui/icons-material/InvertColors";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import FlipIcon from "@mui/icons-material/Flip";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import TuneIcon from "@mui/icons-material/Tune";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import FolderZipIcon from "@mui/icons-material/FolderZip";

import { extractDicomFromZip, renderDicomToCanvas } from "../../utils/dicomParser";

const WINDOW_PRESETS = [
  { label: "Default (DICOM)", key: "default" },
  { label: "Full Dynamic Range (Auto)", key: "auto" },
  { label: "Soft Tissue / Abdomen", key: "soft_tissue", ww: 400, wc: 40 },
  { label: "Bone", key: "bone", ww: 2000, wc: 300 },
  { label: "Lung", key: "lung", ww: 1500, wc: -600 },
  { label: "Brain", key: "brain", ww: 80, wc: 40 },
];

export default function DicomViewerDialog({
  open,
  onClose,
  study,
  onDownloadZip,
}) {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState("");
  const [error, setError] = useState(null);

  const [slices, setSlices] = useState([]);
  const [currentSliceIndex, setCurrentSliceIndex] = useState(0);

  const [windowPreset, setWindowPreset] = useState("default");
  const [windowWidth, setWindowWidth] = useState(400);
  const [windowCenter, setWindowCenter] = useState(40);
  const [invert, setInvert] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [flipH, setFlipH] = useState(false);

  const [isPlayingCine, setIsPlayingCine] = useState(false);
  const [cineFps, setCineFps] = useState(10);
  const cineTimerRef = useRef(null);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showOverlays, setShowOverlays] = useState(true);
  const [showTagDialog, setShowTagDialog] = useState(false);
  const [tagSearch, setTagSearch] = useState("");
  const [activeTool, setActiveTool] = useState("wl");

  const canvasRef = useRef(null);
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const initialWlRef = useRef({ ww: 400, wc: 40 });
  const fileInputRef = useRef(null);

  const currentSlice = slices[currentSliceIndex] || null;
  const currentDicom = currentSlice?.dicom || null;

  const loadZipBuffer = useCallback(async (buffer) => {
    setLoading(true);
    setError(null);
    setLoadingStatus("Decompressing DICOM archive...");
    try {
      const extractedSlices = await extractDicomFromZip(buffer);
      if (extractedSlices.length === 0) {
        throw new Error("No valid DICOM (.dcm) files found inside the ZIP archive.");
      }
      setSlices(extractedSlices);
      setCurrentSliceIndex(0);

      const firstDicom = extractedSlices[0].dicom;
      setWindowWidth(firstDicom.defaultWindowWidth || 400);
      setWindowCenter(firstDicom.defaultWindowCenter || 40);
      setWindowPreset("default");
      setZoom(1);
      setPan({ x: 0, y: 0 });
      setRotation(0);
      setFlipH(false);
      setInvert(false);
    } catch (err) {
      console.error("DICOM extraction error:", err);
      setError(err.message || "Failed to process DICOM ZIP archive.");
    } finally {
      setLoading(false);
      setLoadingStatus("");
    }
  }, []);

  useEffect(() => {
    if (!open || !study) {
      setSlices([]);
      setCurrentSliceIndex(0);
      setError(null);
      setIsPlayingCine(false);
      return;
    }

    if (study.pacsZipDownloadUrl) {
      setActiveTab(0);
      setLoading(true);
      setLoadingStatus("Downloading DICOM files from PACS storage...");
      setError(null);

      fetch(study.pacsZipDownloadUrl)
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}: Failed to download ZIP file`);
          return res.arrayBuffer();
        })
        .then((buffer) => {
          loadZipBuffer(buffer);
        })
        .catch((err) => {
          console.warn("Direct fetch of pacsZipDownloadUrl failed:", err);
          setError(
            "Could not load DICOM ZIP automatically (browser network or CORS restriction). You can download the ZIP directly or upload it below."
          );
          setLoading(false);
          setLoadingStatus("");
        });
    } else if (study.viewer2dUrl) {
      setActiveTab(1);
    } else if (study.viewer3dUrl) {
      setActiveTab(2);
    }
  }, [open, study, loadZipBuffer]);

  const handleLocalFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        loadZipBuffer(event.target.result);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  useEffect(() => {
    if (!canvasRef.current || !currentDicom || activeTab !== 0) return;
    renderDicomToCanvas(currentDicom, canvasRef.current, {
      windowWidth,
      windowCenter,
      invert,
      zoom,
      panX: pan.x,
      panY: pan.y,
      rotation,
      flipH,
    });
  }, [currentDicom, windowWidth, windowCenter, invert, zoom, pan, rotation, flipH, activeTab]);

  useEffect(() => {
    if (isPlayingCine && slices.length > 1) {
      cineTimerRef.current = setInterval(() => {
        setCurrentSliceIndex((prev) => (prev + 1) % slices.length);
      }, Math.max(30, Math.round(1000 / cineFps)));
    } else if (cineTimerRef.current) {
      clearInterval(cineTimerRef.current);
    }
    return () => {
      if (cineTimerRef.current) clearInterval(cineTimerRef.current);
    };
  }, [isPlayingCine, slices.length, cineFps]);

  const handlePresetChange = (e) => {
    const key = e.target.value;
    setWindowPreset(key);
    if (!currentDicom) return;

    if (key === "default") {
      setWindowWidth(currentDicom.defaultWindowWidth);
      setWindowCenter(currentDicom.defaultWindowCenter);
    } else if (key === "auto") {
      const min = currentDicom.minPixel;
      const max = currentDicom.maxPixel;
      setWindowWidth(Math.max(1, max - min));
      setWindowCenter(Math.round((min + max) / 2));
    } else {
      const p = WINDOW_PRESETS.find((x) => x.key === key);
      if (p) {
        setWindowWidth(p.ww);
        setWindowCenter(p.wc);
      }
    }
  };

  const handleMouseDown = (e) => {
    isDraggingRef.current = true;
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    initialWlRef.current = { ww: windowWidth, wc: windowCenter };
  };

  const handleMouseMove = (e) => {
    if (!isDraggingRef.current) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;

    if (activeTool === "wl") {
      const sensitivity = 2;
      const newWw = Math.max(1, Math.round(initialWlRef.current.ww + dx * sensitivity));
      const newWc = Math.round(initialWlRef.current.wc - dy * sensitivity);
      setWindowWidth(newWw);
      setWindowCenter(newWc);
      setWindowPreset("custom");
    } else if (activeTool === "pan") {
      setPan((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
      dragStartRef.current = { x: e.clientX, y: e.clientY };
    } else if (activeTool === "zoom") {
      const zoomFactor = 1 - dy * 0.01;
      setZoom((prev) => Math.min(5, Math.max(0.2, prev * zoomFactor)));
      dragStartRef.current = { x: e.clientX, y: e.clientY };
    }
  };

  const handleMouseUp = () => {
    isDraggingRef.current = false;
  };

  const handleWheel = (e) => {
    if (slices.length <= 1) return;
    e.preventDefault();
    if (e.deltaY > 0) {
      setCurrentSliceIndex((prev) => Math.min(slices.length - 1, prev + 1));
    } else {
      setCurrentSliceIndex((prev) => Math.max(0, prev - 1));
    }
  };

  const handleResetView = () => {
    if (currentDicom) {
      setWindowWidth(currentDicom.defaultWindowWidth);
      setWindowCenter(currentDicom.defaultWindowCenter);
    }
    setWindowPreset("default");
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setRotation(0);
    setFlipH(false);
    setInvert(false);
  };

  const handleExportPng = () => {
    if (!canvasRef.current || !currentDicom) return;
    const link = document.createElement("a");
    link.download = `${study?.testCode || "DICOM"}_slice_${currentSliceIndex + 1}.png`;
    link.href = canvasRef.current.toDataURL("image/png");
    link.click();
  };

  const filteredTags = useMemo(() => {
    if (!currentDicom?.allElements) return [];
    if (!tagSearch) return currentDicom.allElements;
    const query = tagSearch.toLowerCase();
    return currentDicom.allElements.filter(
      (el) =>
        el.tag.toLowerCase().includes(query) ||
        el.name.toLowerCase().includes(query) ||
        el.value.toLowerCase().includes(query)
    );
  }, [currentDicom, tagSearch]);

  if (!study) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isFullscreen}
      maxWidth="xl"
      fullWidth
      sx={{
        zIndex: 99999,
        "& .MuiDialog-paper": {
          bgcolor: "#0f172a",
          color: "#f8fafc",
          height: isFullscreen ? "100vh" : "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: "#1e293b",
          px: 2.5,
          py: 1,
          borderBottom: "1px solid #334155",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ color: "#f8fafc", fontSize: "1rem" }}>
            DICOM Viewer: {study.testName || study.testCode || "Radiology Study"}
          </Typography>
          {study.billId && (
            <Chip
              label={`Bill: ${study.billId}`}
              size="small"
              sx={{ bgcolor: "#334155", color: "#94a3b8", fontSize: "11px" }}
            />
          )}
          {slices.length > 0 && (
            <Chip
              label={`${slices.length} Slice${slices.length > 1 ? "s" : ""}`}
              size="small"
              color="primary"
              sx={{ fontSize: "11px", fontWeight: "bold" }}
            />
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}>
            <IconButton size="small" onClick={() => setIsFullscreen(!isFullscreen)} sx={{ color: "#94a3b8" }}>
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Close Viewer">
            <IconButton size="small" onClick={onClose} sx={{ color: "#94a3b8" }}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      <Box sx={{ bgcolor: "#1e293b", px: 2, borderBottom: "1px solid #334155", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Tabs
          value={activeTab}
          onChange={(e, val) => setActiveTab(val)}
          sx={{
            minHeight: 38,
            "& .MuiTab-root": {
              color: "#94a3b8",
              minHeight: 38,
              py: 0.5,
              textTransform: "none",
              fontSize: "0.85rem",
              fontWeight: 600,
              "&.Mui-selected": { color: "#38bdf8" },
            },
            "& .MuiTabs-indicator": { bgcolor: "#38bdf8" },
          }}
        >
          <Tab label={`In-App DICOM Slices (${slices.length})`} />
          {study.viewer2dUrl && <Tab label="2D Crelio Viewer" />}
          {study.viewer3dUrl && <Tab label="3D Crelio Viewer" />}
        </Tabs>

        {/* {activeTab === 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <input
              type="file"
              ref={fileInputRef}
              accept=".zip"
              style={{ display: "none" }}
              onChange={handleLocalFileSelect}
            />
            <Tooltip title="Load Local DICOM ZIP Archive">
              <Button
                variant="outlined"
                size="small"
                startIcon={<UploadFileIcon sx={{ fontSize: 14 }} />}
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  textTransform: "none",
                  fontSize: "11px",
                  color: "#94a3b8",
                  borderColor: "#475569",
                  py: 0.2,
                }}
              >
                Load ZIP
              </Button>
            </Tooltip>
            {study.pacsZipDownloadUrl && (
              <Tooltip title="Download Original DICOM ZIP">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FolderZipIcon sx={{ fontSize: 14 }} />}
                  onClick={() => onDownloadZip(study.pacsZipDownloadUrl, `${study.testCode || "study"}_dicom.zip`)}
                  sx={{
                    textTransform: "none",
                    fontSize: "11px",
                    color: "#38bdf8",
                    borderColor: "#0284c7",
                    py: 0.2,
                  }}
                >
                  Download ZIP
                </Button>
              </Tooltip>
            )}
          </Box>
        )} */}
      </Box>

      {activeTab === 0 && (
        <DialogContent sx={{ p: 0, flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <Box sx={{ flex: 1, display: "flex", overflow: "hidden", position: "relative" }}>
            {slices.length > 1 && (
              <Box
                sx={{
                  width: 110,
                  bgcolor: "#090d16",
                  borderRight: "1px solid #334155",
                  overflowY: "auto",
                  p: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                {slices.map((slice, idx) => (
                  <Box
                    key={slice.id}
                    onClick={() => setCurrentSliceIndex(idx)}
                    sx={{
                      cursor: "pointer",
                      border: idx === currentSliceIndex ? "2px solid #38bdf8" : "1px solid #334155",
                      borderRadius: "6px",
                      overflow: "hidden",
                      bgcolor: "#1e293b",
                      position: "relative",
                      "&:hover": { borderColor: "#60a5fa" },
                    }}
                  >
                    {slice.thumbnail ? (
                      <img
                        src={slice.thumbnail}
                        alt={`Slice ${idx + 1}`}
                        style={{ width: "100%", height: 75, objectFit: "contain", display: "block" }}
                      />
                    ) : (
                      <Box sx={{ height: 75, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Typography variant="caption" color="text.secondary">Slice {idx + 1}</Typography>
                      </Box>
                    )}
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        bgcolor: "rgba(0,0,0,0.75)",
                        px: 0.5,
                        py: 0.2,
                        textAlign: "center",
                      }}
                    >
                      <Typography sx={{ fontSize: "10px", fontWeight: "bold", color: "#f8fafc" }}>
                        #{idx + 1}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#000000",
                position: "relative",
                overflow: "hidden",
                cursor: activeTool === "pan" ? "grab" : activeTool === "zoom" ? "zoom-in" : "crosshair",
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onWheel={handleWheel}
            >
              {loading ? (
                <Box sx={{ textAlign: "center" }}>
                  <CircularProgress size={42} sx={{ color: "#38bdf8", mb: 2 }} />
                  <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                    {loadingStatus || "Loading DICOM study..."}
                  </Typography>
                </Box>
              ) : error ? (
                <Box sx={{ maxWidth: 450, p: 3, textAlign: "center" }}>
                  <Alert severity="warning" sx={{ mb: 2, textAlign: "left" }}>
                    {error}
                  </Alert>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <Button
                      variant="contained"
                      startIcon={<UploadFileIcon />}
                      onClick={() => fileInputRef.current?.click()}
                      sx={{ textTransform: "none", bgcolor: "#0284c7" }}
                    >
                      Upload / Select Local ZIP File
                    </Button>
                    {study.pacsZipDownloadUrl && (
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={() => onDownloadZip(study.pacsZipDownloadUrl, `${study.testCode || "study"}_dicom.zip`)}
                        sx={{ textTransform: "none", color: "#94a3b8", borderColor: "#475569" }}
                      >
                        Download ZIP to Computer
                      </Button>
                    )}
                    {study.viewer2dUrl && (
                      <Button
                        variant="text"
                        onClick={() => setActiveTab(1)}
                        sx={{ textTransform: "none", color: "#38bdf8" }}
                      >
                        Switch to 2D Crelio Viewer Tab
                      </Button>
                    )}
                  </Box>
                </Box>
              ) : currentDicom ? (
                <>
                  <canvas
                    ref={canvasRef}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                      imageRendering: "pixelated",
                    }}
                  />

                  {showOverlays && (
                    <>
                      <Box sx={{ position: "absolute", top: 12, left: 16, pointerEvents: "none", textShadow: "1px 1px 2px #000" }}>
                        <Typography sx={{ fontSize: "12px", fontWeight: "bold", color: "#e2e8f0" }}>
                          {currentDicom.patientName}
                        </Typography>
                        <Typography sx={{ fontSize: "11px", color: "#94a3b8" }}>
                          ID: {currentDicom.patientId} | {currentDicom.patientSex}
                        </Typography>
                      </Box>

                      <Box sx={{ position: "absolute", top: 12, right: 16, textAlign: "right", pointerEvents: "none", textShadow: "1px 1px 2px #000" }}>
                        <Typography sx={{ fontSize: "12px", fontWeight: "bold", color: "#e2e8f0" }}>
                          {currentDicom.modality} - {currentDicom.studyDescription}
                        </Typography>
                        <Typography sx={{ fontSize: "11px", color: "#94a3b8" }}>
                          {currentDicom.studyDate || ""}
                        </Typography>
                      </Box>

                      <Box sx={{ position: "absolute", bottom: 12, left: 16, pointerEvents: "none", textShadow: "1px 1px 2px #000" }}>
                        <Typography sx={{ fontSize: "11px", color: "#94a3b8" }}>
                          Slice: {currentSliceIndex + 1} / {slices.length}
                        </Typography>
                        <Typography sx={{ fontSize: "11px", color: "#94a3b8" }}>
                          Zoom: {Math.round(zoom * 100)}% | Res: {currentDicom.columns}x{currentDicom.rows}
                        </Typography>
                      </Box>

                      <Box sx={{ position: "absolute", bottom: 12, right: 16, textAlign: "right", pointerEvents: "none", textShadow: "1px 1px 2px #000" }}>
                        <Typography sx={{ fontSize: "11px", color: "#94a3b8" }}>
                          WL: {windowCenter} | WW: {windowWidth}
                        </Typography>
                        <Typography sx={{ fontSize: "10px", color: "#64748b" }}>
                          {invert ? "Inverted" : "Normal"} {rotation > 0 ? `| ${rotation}°` : ""}
                        </Typography>
                      </Box>
                    </>
                  )}
                </>
              ) : (
                <Typography variant="body2" sx={{ color: "#64748b" }}>
                  No DICOM file loaded.
                </Typography>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              bgcolor: "#1e293b",
              px: 2,
              py: 1,
              borderTop: "1px solid #334155",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Tooltip title="Previous Slice">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => setCurrentSliceIndex((prev) => Math.max(0, prev - 1))}
                    disabled={currentSliceIndex <= 0}
                    sx={{ color: "#94a3b8" }}
                  >
                    <NavigateBeforeIcon />
                  </IconButton>
                </span>
              </Tooltip>

              <Typography sx={{ fontSize: "12px", color: "#cbd5e1", minWidth: 70, textAlign: "center" }}>
                {slices.length > 0 ? `${currentSliceIndex + 1} / ${slices.length}` : "0 / 0"}
              </Typography>

              <Tooltip title="Next Slice">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => setCurrentSliceIndex((prev) => Math.min(slices.length - 1, prev + 1))}
                    disabled={currentSliceIndex >= slices.length - 1}
                    sx={{ color: "#94a3b8" }}
                  >
                    <NavigateNextIcon />
                  </IconButton>
                </span>
              </Tooltip>

              {slices.length > 1 && (
                <Box sx={{ width: 100, mx: 1 }}>
                  <Slider
                    size="small"
                    min={0}
                    max={slices.length - 1}
                    value={currentSliceIndex}
                    onChange={(e, val) => setCurrentSliceIndex(val)}
                    sx={{ color: "#38bdf8" }}
                  />
                </Box>
              )}

              {slices.length > 1 && (
                <Tooltip title={isPlayingCine ? "Pause Cine Loop" : "Play Cine Loop"}>
                  <IconButton
                    size="small"
                    onClick={() => setIsPlayingCine(!isPlayingCine)}
                    sx={{ color: isPlayingCine ? "#38bdf8" : "#94a3b8" }}
                  >
                    {isPlayingCine ? <PauseIcon /> : <PlayArrowIcon />}
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Tooltip title="Window Level Tool (Drag on canvas to adjust contrast/brightness)">
                <Button
                  size="small"
                  variant={activeTool === "wl" ? "contained" : "outlined"}
                  onClick={() => setActiveTool("wl")}
                  sx={{
                    textTransform: "none",
                    fontSize: "11px",
                    py: 0.3,
                    px: 1,
                    minWidth: "auto",
                    bgcolor: activeTool === "wl" ? "#0284c7" : "transparent",
                    borderColor: "#475569",
                    color: activeTool === "wl" ? "#fff" : "#94a3b8",
                  }}
                >
                  W/L
                </Button>
              </Tooltip>
              <Tooltip title="Pan Tool (Drag to move image)">
                <Button
                  size="small"
                  variant={activeTool === "pan" ? "contained" : "outlined"}
                  onClick={() => setActiveTool("pan")}
                  sx={{
                    textTransform: "none",
                    fontSize: "11px",
                    py: 0.3,
                    px: 1,
                    minWidth: "auto",
                    bgcolor: activeTool === "pan" ? "#0284c7" : "transparent",
                    borderColor: "#475569",
                    color: activeTool === "pan" ? "#fff" : "#94a3b8",
                  }}
                >
                  Pan
                </Button>
              </Tooltip>
              <Tooltip title="Zoom Tool (Drag up/down to zoom)">
                <Button
                  size="small"
                  variant={activeTool === "zoom" ? "contained" : "outlined"}
                  onClick={() => setActiveTool("zoom")}
                  sx={{
                    textTransform: "none",
                    fontSize: "11px",
                    py: 0.3,
                    px: 1,
                    minWidth: "auto",
                    bgcolor: activeTool === "zoom" ? "#0284c7" : "transparent",
                    borderColor: "#475569",
                    color: activeTool === "zoom" ? "#fff" : "#94a3b8",
                  }}
                >
                  Zoom
                </Button>
              </Tooltip>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TuneIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
              <Select
                size="small"
                value={windowPreset}
                onChange={handlePresetChange}
                sx={{
                  color: "#f8fafc",
                  fontSize: "11px",
                  height: 28,
                  bgcolor: "#0f172a",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#475569" },
                  "& .MuiSvgIcon-root": { color: "#94a3b8" },
                }}
              >
                {WINDOW_PRESETS.map((p) => (
                  <MenuItem key={p.key} value={p.key} sx={{ fontSize: "11px" }}>
                    {p.label}
                  </MenuItem>
                ))}
                {windowPreset === "custom" && (
                  <MenuItem value="custom" sx={{ fontSize: "11px" }}>
                    Custom (W:{windowWidth} C:{windowCenter})
                  </MenuItem>
                )}
              </Select>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Tooltip title="Zoom In">
                <IconButton size="small" onClick={() => setZoom((z) => Math.min(5, z * 1.25))} sx={{ color: "#94a3b8" }}>
                  <ZoomInIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Zoom Out">
                <IconButton size="small" onClick={() => setZoom((z) => Math.max(0.2, z * 0.8))} sx={{ color: "#94a3b8" }}>
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {/* <Tooltip title="Invert Colors (Negative/Positive)">
                <IconButton size="small" onClick={() => setInvert(!invert)} sx={{ color: invert ? "#38bdf8" : "#94a3b8" }}>
                  <InvertColorsIcon fontSize="small" />
                </IconButton>
              </Tooltip> */}
              <Tooltip title="Rotate 90° Clockwise">
                <IconButton size="small" onClick={() => setRotation((r) => (r + 90) % 360)} sx={{ color: "#94a3b8" }}>
                  <RotateRightIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Horizontal Flip">
                <IconButton size="small" onClick={() => setFlipH(!flipH)} sx={{ color: flipH ? "#38bdf8" : "#94a3b8" }}>
                  <FlipIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Toggle Overlays">
                <IconButton size="small" onClick={() => setShowOverlays(!showOverlays)} sx={{ color: showOverlays ? "#38bdf8" : "#94a3b8" }}>
                  <ViewModuleIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Reset View">
                <IconButton size="small" onClick={handleResetView} sx={{ color: "#94a3b8" }}>
                  <RestartAltIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {/* <Tooltip title="Inspect DICOM Header Tags">
                <IconButton size="small" onClick={() => setShowTagDialog(true)} sx={{ color: "#94a3b8" }}>
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip> */}
              {/* <Tooltip title="Export Current Slice as PNG">
                <IconButton size="small" onClick={handleExportPng} sx={{ color: "#94a3b8" }}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip> */}
            </Box>
          </Box>
        </DialogContent>
      )}

      {activeTab === 1 && study.viewer2dUrl && (
        <DialogContent sx={{ p: 0, flex: 1, position: "relative" }}>
          <iframe
            src={study.viewer2dUrl}
            title="Crelio 2D PACS Viewer"
            style={{ width: "100%", height: "100%", border: "none", backgroundColor: "#000" }}
          />
          <Box sx={{ position: "absolute", top: 8, right: 8 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<OpenInNewIcon />}
              onClick={() => globalThis.open(study.viewer2dUrl, "_blank")}
              sx={{ textTransform: "none", fontSize: "11px", bgcolor: "rgba(30,41,59,0.9)" }}
            >
              Open in New Window
            </Button>
          </Box>
        </DialogContent>
      )}

      {activeTab === 2 && study.viewer3dUrl && (
        <DialogContent sx={{ p: 0, flex: 1, position: "relative" }}>
          <iframe
            src={study.viewer3dUrl}
            title="Crelio 3D PACS Viewer"
            style={{ width: "100%", height: "100%", border: "none", backgroundColor: "#000" }}
          />
          <Box sx={{ position: "absolute", top: 8, right: 8 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<OpenInNewIcon />}
              onClick={() => globalThis.open(study.viewer3dUrl, "_blank")}
              sx={{ textTransform: "none", fontSize: "11px", bgcolor: "rgba(30,41,59,0.9)" }}
            >
              Open in New Window
            </Button>
          </Box>
        </DialogContent>
      )}

      {/* <Dialog
        open={showTagDialog}
        onClose={() => setShowTagDialog(false)}
        maxWidth="md"
        fullWidth
        sx={{ zIndex: 100000 }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Typography variant="h6" fontWeight="bold">
            DICOM Header Tags (Slice #{currentSliceIndex + 1})
          </Typography>
          <IconButton size="small" onClick={() => setShowTagDialog(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            size="small"
            fullWidth
            placeholder="Search tags by name, tag number, or value..."
            value={tagSearch}
            onChange={(e) => setTagSearch(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Tag</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>VR</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTags.map((el, idx) => (
                  <TableRow key={`${el.tag}-${idx}`} hover>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: "11px", color: "primary.main" }}>
                      ({el.tag})
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500, fontSize: "12px" }}>{el.name}</TableCell>
                    <TableCell sx={{ fontSize: "11px", color: "text.secondary" }}>{el.vr}</TableCell>
                    <TableCell sx={{ fontSize: "11px", wordBreak: "break-all" }}>{el.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog> */}
    </Dialog>
  );
}

DicomViewerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  study: PropTypes.object,
  onDownloadZip: PropTypes.func.isRequired,
};
